import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  DOMAttributes,
  HTMLAttributes,
  DetailedHTMLProps,
  useCallback
} from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import { RouteComponentProps } from 'react-router-dom';
import { type VideoPlayerContainer, type VideoPlayer, VideoQuality } from '@zoom/videosdk';
import ZoomContext from '../../context/zoom-context';
import ZoomMediaContext from '../../context/media-context';
import AvatarActionContext from './context/avatar-context';
import ShareView from './components/share-view';
import VideoFooter from './components/video-footer';
import Avatar from './components/avatar';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useAvatarAction } from './hooks/useAvatarAction';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import { useParticipantsChange } from './hooks/useParticipantsChange';
import { Participant } from '../../index-types';
import { useOrientation, usePrevious } from '../../hooks';
import { useVideoAspect } from './hooks/useVideoAspectRatio';
import { Radio, Button } from 'antd';

type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<VideoPlayer>, VideoPlayer> & { class?: string };
      ['video-player-container']: CustomElement<VideoPlayerContainer> & { class?: string };
    }
  }
}

const VideoContainer: React.FunctionComponent<RouteComponentProps> = (props) => {
  const zmClient = useContext(ZoomContext);
  const { mediaStream } = useContext(ZoomMediaContext);
  const shareViewRef = useRef<{ selfShareRef: HTMLCanvasElement | HTMLVideoElement | null }>(null);
  const videoPlayerListRef = useRef<Record<string, VideoPlayer>>({});
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const [participants, setParticipants] = useState(zmClient.getAllUser());
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 9;
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedParticipants = participants.slice(startIndex, endIndex);
  const totalPages = Math.ceil(participants.length / itemsPerPage);
  const activeVideo = useActiveVideo(zmClient);
  const avatarActionState = useAvatarAction(zmClient, participants, true);
  const networkQuality = useNetworkQuality(zmClient);
  const previousSubscribers = usePrevious(paginatedParticipants.map(p => p.userId));
  const aspectRatio = useVideoAspect(zmClient);
  const optionsOfVideoResolution = [
    { label: '720P', value: VideoQuality.Video_720P },
    { label: '360P', value: VideoQuality.Video_360P },
    { label: '180P', value: VideoQuality.Video_180P },
    { label: '90P', value: VideoQuality.Video_90P }
  ];
  const orientation = useOrientation();
  const maxVideoCellWidth = orientation === 'portrait' ? 'none' : `calc(100vw/${Math.min(participants.length, 4)})`;

  useParticipantsChange(zmClient, (participants) => {
    let pageParticipants: Participant[] = [];
    if (participants.length > 0) {
      if (participants.length === 1) {
        pageParticipants = participants;
      } else {
        pageParticipants = participants
          .filter((user) => user.userId !== zmClient.getSessionInfo().userId)
          .sort((user1, user2) => Number(user2.bVideoOn) - Number(user1.bVideoOn));
        const currentUser = zmClient.getCurrentUserInfo();
        if (currentUser) {
          pageParticipants.splice(1, 0, currentUser);
        }
      }
    }
    setParticipants(pageParticipants);
  });

  const setVideoPlayerRef = (userId: number, element: VideoPlayer | null) => {
    if (element) {
      videoPlayerListRef.current[`${userId}`] = element;
    }
  };

  const onVideoResolutionChange = useCallback(
    ({ target: { value } }: any, userId: number) => {
      const attachment = videoPlayerListRef.current[`${userId}`];
      mediaStream?.attachVideo(userId, value, attachment);
    },
    [videoPlayerListRef, mediaStream]
  );

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    const paginatedUserIds = paginatedParticipants.map(p => p.userId);
    const addedUsers = paginatedParticipants.filter(p => !(previousSubscribers || []).includes(p.userId));
    const removedUsers = (previousSubscribers || []).filter(userId => !paginatedUserIds.includes(userId));
  
    // Detach video streams for users that are no longer in the current page
    if (removedUsers.length > 0) {
      removedUsers.forEach((userId) => {
        mediaStream?.detachVideo(userId);
      });
    }
  
    // Attach video streams for newly added users or users in the current page
    const attachVideoToUsers = (users: Participant[]) => {
      users.forEach(({ userId, bVideoOn }) => {
        if (bVideoOn) {
          const attachment = videoPlayerListRef.current[`${userId}`];
          if (attachment) {
            mediaStream?.attachVideo(userId, VideoQuality.Video_360P, attachment).catch(err => {
              console.error(`Failed to attach video for userId ${userId}:`, err);
            });
          }
        }
      });
    };
  
    attachVideoToUsers(paginatedParticipants);
  
  }, [paginatedParticipants, previousSubscribers, mediaStream]);
  

  return (
    <div className="viewport" style={{ width: 'auto' }}>
      <ShareView ref={shareViewRef} onRecieveSharingChange={setIsRecieveSharing} />
      <div
        className={classnames('video-container', 'video-container-attech', {
          'video-container-in-sharing': isRecieveSharing
        })}
      >
        <video-player-container class="video-container-wrap">
          <AvatarActionContext.Provider value={avatarActionState}>
            <ul className="user-list">
              {paginatedParticipants.map((user) => {               
                return (
                  <div
                    className={paginatedParticipants.length === 1 ? "video-cell single-frame" : "video-cell"}
                    key={user.userId}
                  >
                    {avatarActionState?.avatarActionState[user?.userId]?.videoResolutionAdjust?.toggled && (
                      <div className="change-video-resolution">
                        <Radio.Group
                          options={optionsOfVideoResolution}
                          onChange={(value) => {
                            onVideoResolutionChange(value, user.userId);
                          }}
                          defaultValue={VideoQuality.Video_360P}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </div>
                    )}
                    {user.bVideoOn && (
                      <div>
                        <video-player
                          class="video-player"
                          ref={(element) => {
                            setVideoPlayerRef(user.userId, element);
                          }}
                        />
                      </div>
                    )}
                    <Avatar
                      participant={user}
                      key={user.userId}
                      isActive={activeVideo === user.userId}
                      networkQuality={networkQuality[`${user.userId}`]}
                    />
                  </div>
                );
              })}
            </ul>
            <div className="pagination-controls">
              <Button onClick={handlePreviousPage} disabled={currentPage === 0}>
                &laquo;
              </Button>
              <span className="page-info" style={{ color: "white" }}>
                Page {currentPage + 1} of {totalPages} | Total: {participants.length}
              </span>
              <Button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages - 1}
              >
                &raquo;
              </Button>
            </div>
          </AvatarActionContext.Provider>
        </video-player-container>
      </div>
      <VideoFooter className="video-operations" sharing selfShareCanvas={shareViewRef.current?.selfShareRef} />
    </div>
  );
};

export default VideoContainer;
